import React, { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "@amzn/afxtaco-utilities";
import { Button } from "@/components/ui/button";
import {
  Bot,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import { Message, ActionCard as ActionCardType } from "@/types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ActionCard from "./ActionCard";
import { useTheme } from "next-themes";
import { SimpleFeedback } from "@amzn/afxtaco-types";
import { postInteraction } from "@/api/interaction/interaction";
import { preprocessChatContent } from "@/utils/markdown";
import WorkingIndicator from "./chat/WorkingIndicator";
import ToolChevron from "./chat/ToolChevron";
import { collapseChatItems } from "@/utils/toolChevrons";

interface AIResponseProps {
  message: Message;
  onActionCardClick?: (card: ActionCardType) => void;
  activeCardId?: string;
}

const AIResponse = ({
  message,
  onActionCardClick,
  activeCardId,
}: AIResponseProps) => {
  const { theme } = useTheme();
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [visibleStepIndex, setVisibleStepIndex] = useState(-1);
  const [showFinalMessage, setShowFinalMessage] = useState(false);
  const [feedbackState, setFeedbackState] = useState<SimpleFeedback | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up copy indicator timer on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Handle sequential display of thinking steps
  useEffect(() => {
    if (
      message.showThinking &&
      message.thinkingSteps &&
      message.thinkingSteps.length > 0
    ) {
      setVisibleStepIndex(-1);
      setShowFinalMessage(false);

      const steps = message.thinkingSteps; // Store reference to avoid undefined issues
      let currentIndex = -1;
      const intervalId = setInterval(() => {
        currentIndex++;
        if (currentIndex < steps.length) {
          setVisibleStepIndex(currentIndex);
        } else {
          clearInterval(intervalId);
          // Show final message after a brief delay
          setTimeout(() => {
            setShowFinalMessage(true);
          }, 500);
        }
      }, 1000); // 1 second delay between steps

      return () => clearInterval(intervalId);
    } else {
      // If no thinking steps, show message immediately
      setShowFinalMessage(true);
    }
  }, [message.id, message.showThinking, message.thinkingSteps]);

  // Render content as markdown using ReactMarkdown.
  // All AI responses are treated as markdown — ReactMarkdown with remark-gfm
  // handles tables, headers, lists, code blocks, horizontal rules, etc.
  // Angle brackets in code spans (e.g. `OFFICE#<id>`) render as literal text.
  // Any stray HTML-like tags outside code spans are safely stripped by ReactMarkdown.
  const renderContent = (content: string) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-base font-semibold mb-2 text-gray-900 dark:text-white">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold mb-1 text-gray-900 dark:text-white">
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className="text-sm mb-2 last:mb-0 text-gray-900 dark:text-white">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="mb-2 ml-4 list-disc">{children}</ul>
          ),
          ol: ({ children, start }) => (
            <ol start={start} className="mb-2 ml-4 list-decimal">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-sm text-gray-900 dark:text-white mb-1">
              {children}
            </li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900 dark:text-white">
              {children}
            </strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          // Block code renders as <pre><code>.
          // Style the block on <pre> and reset <code> inside it
          pre: ({ children }) => (
            <pre className="mb-2 overflow-x-auto bg-muted p-3 rounded-lg text-xs font-mono [&>code]:bg-transparent [&>code]:p-0 [&>code]:rounded-none [&>code]:break-normal">
              {children}
            </pre>
          ),
          code: ({ children }) => (
            <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono break-words">
              {children}
            </code>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-muted-foreground pl-4 italic text-muted-foreground mb-2">
              {children}
            </blockquote>
          ),
          hr: () => (
            <hr className="my-4 border-gray-300 dark:border-gray-600" />
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          // Hide image references that have no valid src (AI sometimes outputs
          // markdown image syntax like ![diagram] without an actual URL)
          img: ({ src, alt }) =>
            src ? (
              <img src={src} alt={alt || ""} className="max-w-full rounded" />
            ) : null,
          table: ({ children }) => (
            <div className="overflow-x-auto mb-2">
              <table className="w-full text-sm border-collapse border border-gray-300 dark:border-gray-600 rounded-lg">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-medium text-gray-900 dark:text-white">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-white">
              {children}
            </td>
          ),
        }}
      >
        {preprocessChatContent(content)}
      </ReactMarkdown>
    );
  };

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      // Clear any existing timer so rapid clicks restart the 2s window
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      // Clipboard write can fail in non-secure contexts or if permission is denied.
      // No user-facing indicator — the button simply won't show "copied".
      logger.error({ loggerMsg: "Failed to copy to clipboard", error });
    }
  }, [message.content]);

  const handleFeedback = async (type: SimpleFeedback) => {
    // Ignore if user presses the same feedback button again
    if (feedbackState === type) {
      return;
    }

    setFeedbackState(type);

    try {
      await postInteraction({ simpleFeedback: type });
    } catch (error) {
      logger.error({ loggerMsg: "Failed to submit feedback", error });
      // Revert on failure
      setFeedbackState(null);
    }
  };

  const handleActionCardClick = (card: ActionCardType) => {
    if (onActionCardClick) {
      onActionCardClick(card);
    }
  };

  // `thinkingChevron`: split the ordered timeline into everything up to the
  // turn's final text run (rendered above, with chevrons interleaved) and that
  // final text (rendered in the shared block, which also carries action cards,
  // sources, and feedback controls).
  const collapsedItems = message.chatItems
    ? collapseChatItems(message.chatItems)
    : [];
  const hasChevronItems = collapsedItems.length > 0;
  const lastItem = collapsedItems[collapsedItems.length - 1];
  const trailingChevronText = lastItem?.kind === "text" ? lastItem.content : "";
  const leadingChevronItems =
    lastItem?.kind === "text" ? collapsedItems.slice(0, -1) : collapsedItems;

  // Whether a tool call is actively running right now — the trailing chevron
  // shows its own spinner in that window, so the turn-level indicator stands
  // down to avoid two spinners.
  const hasRunningTool =
    lastItem?.kind === "tools" &&
    lastItem.calls.some((call) => call.status === "running");

  // The turn is still in flight but nothing else is animating: a tool cluster
  // just finished and the agent hasn't produced the next text/tool yet, or it's
  // deciding what to do. Without this the UI goes static between tool calls.
  const showTurnIndicator =
    message.isLoading &&
    hasChevronItems &&
    !hasRunningTool &&
    (lastItem?.kind !== "text" || !trailingChevronText.trim());

  // Show a clear "working" indicator when loading AND no content has arrived yet.
  // Once streaming content starts arriving, fall through to render.
  if (message.isLoading && !message.content && !hasChevronItems) {
    return <WorkingIndicator />;
  }

  return (
    <div className="mb-16">
      {/* Thinking Steps Panel */}
      {message.showThinking &&
        message.thinkingSteps &&
        message.thinkingSteps.length > 0 && (
          <div className="relative mb-4 rounded-xl overflow-hidden border border-purple-200/50 dark:border-purple-500/30 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-gray-800 dark:via-gray-850 dark:to-gray-900 shadow-lg">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-blue-500/5 to-indigo-500/5 dark:from-purple-500/10 dark:via-blue-500/10 dark:to-indigo-500/10 animate-pulse" />

            <div className="relative px-5 py-4">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-purple-500/20 dark:bg-purple-400/30 blur-lg animate-pulse" />
                  <div className="relative bg-gradient-to-br from-purple-600 to-indigo-700 dark:from-purple-500 dark:to-indigo-600 p-2 rounded-lg">
                    <Bot
                      className="h-4 w-4 text-white stroke-white"
                      strokeWidth={2.5}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold bg-gradient-to-r from-purple-700 via-blue-600 to-indigo-700 dark:from-purple-300 dark:via-blue-300 dark:to-indigo-300 bg-clip-text text-transparent">
                    Thinking Process
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-white mt-0.5">
                    Analyzing your request step by step
                  </p>
                </div>
                <Loader2 className="h-4 w-4 text-purple-600 dark:text-purple-400 animate-spin" />
              </div>

              {/* Steps with connecting line */}
              <div className="space-y-0 relative">
                {/* Vertical connecting line */}
                <div className="absolute left-[15px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-purple-300 via-blue-300 to-indigo-300 dark:from-purple-600 dark:via-blue-600 dark:to-indigo-600 opacity-50" />

                {message.thinkingSteps
                  .slice(0, visibleStepIndex + 1)
                  .map((step, index) => {
                    const isCompleted = index < visibleStepIndex;
                    const isActive = index === visibleStepIndex;

                    return (
                      <div
                        key={step.id}
                        className={`relative flex items-start gap-3 py-2.5 px-3 rounded-lg transition-all duration-500 ${
                          isActive
                            ? "bg-white/60 dark:bg-gray-800/60 shadow-sm"
                            : ""
                        }`}
                      >
                        {/* Icon */}
                        <div className="relative flex-shrink-0 mt-0.5">
                          {isCompleted ? (
                            theme === "dark" ? (
                              <Check
                                className="h-5 w-5 text-green-400"
                                strokeWidth={2.5}
                                style={{ fill: "none", stroke: "currentColor" }}
                              />
                            ) : (
                              <Check
                                className="h-5 w-5 text-green-600"
                                strokeWidth={2.5}
                                style={{ fill: "none", stroke: "currentColor" }}
                              />
                            )
                          ) : theme === "dark" ? (
                            <Loader2
                              className="h-5 w-5 text-purple-400 animate-spin"
                              strokeWidth={2.5}
                              style={{ fill: "none", stroke: "currentColor" }}
                            />
                          ) : (
                            <Loader2
                              className="h-5 w-5 text-purple-600 animate-spin"
                              strokeWidth={2.5}
                              style={{ fill: "none", stroke: "currentColor" }}
                            />
                          )}
                        </div>

                        {/* Step text */}
                        <span
                          className={`text-sm leading-relaxed transition-all duration-300 ${
                            isCompleted
                              ? "text-gray-700 dark:text-gray-100 font-medium"
                              : "text-gray-900 dark:text-gray-50 font-semibold"
                          }`}
                        >
                          {step.text}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

      {/* `thinkingChevron`: everything before the turn's final text — earlier
          narration and the tool chevrons between it — rendered in the agent's
          generation order. The trailing text falls through to the shared block
          below so action cards, sources, and feedback stay attached to it. */}
      {leadingChevronItems.length > 0 && (
        <div className="space-y-2 mb-2">
          {leadingChevronItems.map((item, index) =>
            item.kind === "tools" ? (
              <ToolChevron key={`tools-${index}`} calls={item.calls} />
            ) : (
              <div
                key={`text-${index}`}
                className="bg-gray-50 dark:bg-[#0F141A] rounded-lg px-4 py-3 overflow-hidden"
              >
                <div className="prose prose-sm max-w-none overflow-x-auto">
                  {renderContent(item.content)}
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {/* Turn-level activity. Covers the gaps the chevron spinner doesn't: after
          a cluster completes and before the next text or tool call arrives. No
          live tool detail here by definition (nothing is running), so it cycles
          the default labels. */}
      {showTurnIndicator && (
        <div className="mb-2">
          <WorkingIndicator />
        </div>
      )}

      {/* Message content - show when thinking is complete OR when streaming content is arriving */}
      {(showFinalMessage ||
        (message.isLoading && message.content) ||
        trailingChevronText) && (
        <>
          {(trailingChevronText || !hasChevronItems) && (
            <div className="bg-gray-50 dark:bg-[#0F141A] rounded-lg px-4 py-3 overflow-hidden">
              <div className="prose prose-sm max-w-none overflow-x-auto">
                {renderContent(
                  hasChevronItems ? trailingChevronText : message.content,
                )}
                {/* Blinking cursor while content is still streaming */}
                {message.isLoading && message.content && (
                  <span className="inline-block w-2 h-4 ml-0.5 bg-gray-400 dark:bg-gray-500 animate-pulse rounded-sm align-text-bottom" />
                )}
              </div>
            </div>
          )}

          {/* Action Cards — only show after streaming is complete */}
          {!message.isLoading &&
            message.actionCards &&
            message.actionCards.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.actionCards.map((card) => (
                  <ActionCard
                    key={card.id}
                    card={card}
                    onClick={handleActionCardClick}
                    isActive={activeCardId === card.id}
                  />
                ))}
              </div>
            )}

          {/* Action buttons and sources row — only show after streaming is complete */}
          {!message.isLoading && (
            <>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-3">
                  {/* Sources button */}
                  {message.sources && message.sources.length > 0 && (
                    <Button
                      variant={"outline"}
                      onClick={() => setSourcesExpanded(!sourcesExpanded)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      {sourcesExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                      Sources ({message.sources.length})
                    </Button>
                  )}
                </div>

                {/* Thumbs up, thumbs down, and copy buttons */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 w-8 p-0 ${
                      feedbackState === SimpleFeedback.POSITIVE
                        ? "text-green-600 border-green-600 dark:text-green-400 dark:border-green-400"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => handleFeedback(SimpleFeedback.POSITIVE)}
                  >
                    <ThumbsUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 w-8 p-0 ${
                      feedbackState === SimpleFeedback.NEGATIVE
                        ? "text-red-600 border-red-600 dark:text-red-400 dark:border-red-400"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => handleFeedback(SimpleFeedback.NEGATIVE)}
                  >
                    <ThumbsDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 w-8 p-0 ${
                      copied
                        ? "text-green-600 border-green-600 dark:text-green-400 dark:border-green-400"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Expanded sources */}
              {sourcesExpanded &&
                message.sources &&
                message.sources.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {message.sources.map((source, index) => (
                      <div
                        key={source.id}
                        className="flex items-center gap-4 text-xs text-muted-foreground"
                      >
                        <div className="flex flex-col gap-1 flex-1">
                          <div className="flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 gap-1">
                            <p className="font-medium text-sm">
                              [{index + 1}] {source.title}
                            </p>
                            <ExternalLink className="h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AIResponse;
